import React from 'react';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ReceiptIcon from '@mui/icons-material/Receipt';
import dayjs from 'dayjs';
import type { ColDef } from 'ag-grid-community';
import type { Expense } from '../../../types/expense';
import type { Supplier } from '../../../types/supplier';
import type { CategoryOption } from '../../../constants/khaataCategories';
import { formatCurrency } from '@utilities/formatCurrency';
import { formatCategoryLabel, getTypeLabel } from '../../../constants/khaataCategories';
import { getExpenseType } from '../../../types/expense';

export type ExpenseGridRow = Expense & { isTotalRow?: boolean };

export type ExpenseColDefsOptions = {
    onEdit: (expense: Expense) => void;
    onDelete: (expenseId: string) => void;
    onViewBill: (expense: Expense) => void;
    isMobile?: boolean;
    getSuppliers?: () => Supplier[];
    getCategoryOptions?: () => CategoryOption[] | undefined;
};

/** "Contact Person, Supplier Name" (falls back to whichever is available). */
const formatSupplierCell = (
    row: ExpenseGridRow | undefined,
    contactBySupplierId: Map<string, string>
): string => {
    if (!row || isTotalRow(row)) return '';
    const supplierName = row.supplierName || '';
    if (!supplierName) return '—';
    const contactPerson =
        (row.supplierId && contactBySupplierId.get(row.supplierId)) || '';
    return contactPerson ? `${contactPerson}, ${supplierName}` : supplierName;
};

const isTotalRow = (data?: ExpenseGridRow) => data?.id === 'Total:';

const BillRenderer = (props: { data?: ExpenseGridRow; onViewBill?: (expense: Expense) => void }) => {
    const { data, onViewBill } = props;
    if (!data || isTotalRow(data)) {
        return React.createElement('span', null, '');
    }
    if (!data.billUrl) {
        return React.createElement('span', { className: 'expense-grid__muted' }, '—');
    }
    return React.createElement(
        'button',
        {
            type: 'button',
            className: 'expense-grid__link',
            onClick: () => onViewBill?.(data),
        },
        React.createElement(ReceiptIcon, { fontSize: 'small' }),
        ' View bill'
    );
};

const ActionsRenderer = (props: {
    data?: ExpenseGridRow;
    onEdit?: (expense: Expense) => void;
    onDelete?: (expenseId: string) => void;
}) => {
    const { data, onEdit, onDelete } = props;
    if (!data || isTotalRow(data)) {
        return React.createElement('span', null, '');
    }
    return React.createElement(
        'div',
        { className: 'expense-grid__actions' },
        React.createElement(IconButton, {
            size: 'small',
            'aria-label': 'Edit expense',
            onClick: () => onEdit?.(data),
        }, React.createElement(EditIcon, { fontSize: 'small' })),
        React.createElement(IconButton, {
            size: 'small',
            color: 'error',
            'aria-label': 'Delete expense',
            onClick: () => onDelete?.(data.id),
        }, React.createElement(DeleteForeverIcon, { fontSize: 'small' }))
    );
};

const formatPayment = (value?: string) =>
    (value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const getExpenseColDefs = ({
    onEdit,
    onDelete,
    onViewBill,
    isMobile = false,
    getSuppliers,
    getCategoryOptions,
}: ExpenseColDefsOptions): ColDef<ExpenseGridRow>[] => {
    const resolveContactMap = () =>
        new Map(
            (getSuppliers?.() ?? [])
                .filter((s) => s.contactPerson)
                .map((s) => [s.id, s.contactPerson as string])
        );

    return [
    {
        headerName: 'Date',
        field: 'date',
        minWidth: 118,
        flex: isMobile ? 1.1 : 1,
        valueFormatter: (p) =>
            isTotalRow(p.data) ? '' : (p.value ? dayjs(p.value).format('MMM DD, YYYY') : ''),
        comparator: (_a, _b, nodeA, nodeB) =>
            dayjs(nodeA?.data?.date || 0).valueOf() - dayjs(nodeB?.data?.date || 0).valueOf(),
    },
    {
        headerName: 'Type',
        field: 'type',
        minWidth: 110,
        flex: 0.9,
        hide: isMobile,
        valueFormatter: (p) => {
            if (isTotalRow(p.data)) return '';
            return getTypeLabel(getExpenseType(p.data || { type: 'expense' }));
        },
    },
    {
        headerName: 'Category',
        field: 'category',
        minWidth: 130,
        flex: 1.1,
        valueFormatter: (p) =>
            isTotalRow(p.data)
                ? 'Total'
                : (p.data?.categoryLabel ||
                    formatCategoryLabel(p.value, getCategoryOptions?.())),
    },
    {
        headerName: 'Supplier',
        field: 'supplierName',
        minWidth: 150,
        flex: 1.2,
        hide: isMobile,
        tooltipValueGetter: (p) => formatSupplierCell(p.data, resolveContactMap()),
        valueGetter: (p) => formatSupplierCell(p.data, resolveContactMap()),
    },
    {
        headerName: 'Description',
        field: 'description',
        minWidth: 140,
        flex: isMobile ? 0 : 1.4,
        hide: isMobile,
        tooltipField: 'description',
        valueFormatter: (p) => (isTotalRow(p.data) ? '' : (p.value || '—')),
    },
    {
        headerName: 'Amount',
        field: 'amount',
        minWidth: 108,
        flex: 0.9,
        type: 'numericColumn',
        cellClass: (p) => (isTotalRow(p.data) ? 'expense-grid__amount-total' : 'expense-grid__amount'),
        valueFormatter: (p) => (typeof p.value === 'number' ? formatCurrency(p.value) : ''),
        comparator: (a, b) => Number(a || 0) - Number(b || 0),
    },
    {
        headerName: 'Payment',
        field: 'paymentMethod',
        minWidth: 100,
        flex: 0.9,
        hide: isMobile,
        valueFormatter: (p) => (isTotalRow(p.data) ? '' : formatPayment(p.value)),
    },
    {
        headerName: 'Added By',
        field: 'userName',
        minWidth: 120,
        flex: 1,
        hide: isMobile,
        valueFormatter: (p) => (isTotalRow(p.data) ? '' : (p.value || '—')),
    },
    {
        headerName: 'Bill',
        colId: 'bill',
        width: 96,
        minWidth: 88,
        maxWidth: 110,
        sortable: false,
        suppressMovable: true,
        cellRenderer: BillRenderer,
        cellRendererParams: { onViewBill },
    },
    {
        headerName: 'Actions',
        colId: 'actions',
        width: 96,
        minWidth: 88,
        maxWidth: 110,
        sortable: false,
        suppressMovable: true,
        pinned: isMobile ? 'right' : undefined,
        cellRenderer: ActionsRenderer,
        cellRendererParams: { onEdit, onDelete },
    },
    ];
};

export const buildExpensePinnedTotalRow = (totalAmount: number): ExpenseGridRow => ({
    id: 'Total:',
    date: '',
    userId: '',
    userName: '',
    restId: '',
    type: 'expense',
    category: 'Total',
    description: '',
    amount: totalAmount,
    paymentMethod: 'cash',
});
